import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import axios from "axios";

import "../../Styles/UserAuthStyles/AuthModal.css";
import "../../Styles/UserAuthStyles/Login.css";
import "../../Styles/UserAuthStyles/Signup.css";
import { useUser } from "../../Context/UserContext";
import { api, setTokens } from "../../api";

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
  initialEmail?: string;
}

const getPasswordStrength = (pwd: string): { label: string; color: string; percent: number } => {
  if (!pwd) return { label: '', color: 'transparent', percent: 0 };
  if (pwd.length < 8) return { label: 'Too short (min 8 characters)', color: '#dc2626', percent: 33 };
  if (pwd.length <= 10) return { label: 'Acceptable', color: '#f59e0b', percent: 66 };
  return { label: 'Strong', color: '#22c55e', percent: 100 };
};

function SignupModal({ isOpen, onClose, onSwitchToLogin, initialEmail }: SignupModalProps) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const { fetchUser } = useUser();

  // Populate email when modal opens with initialEmail
  useEffect(() => {
    if (isOpen && initialEmail) {
      setEmail(initialEmail);
    }
  }, [isOpen, initialEmail]);

  // Reset form fields when modal closes (keep email so hero input value persists)
  useEffect(() => {
    if (!isOpen) {
      setName("");
      setPassword("");
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleSignupEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await api.post("/api/signup", {
        loginMethod: "email",
        name,
        email,
        password,
      });

      toast.success("Account created! Check your email to verify your address.");
      onSwitchToLogin();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        const message: string = err.response.data?.message ?? "";
        toast.error(message || "An unexpected error occurred. Please try again.");
      } else {
        console.error(err);
        toast.error("Unable to connect. Please check your internet connection and try again.");
      }
    }
  };

  interface GoogleJwtPayload {
    name?: string;
    email?: string;
  }

  const handleSignupGoogle = async (googleCredential: string) => {
    try {
      const decoded = jwtDecode<GoogleJwtPayload>(googleCredential);

      const res = await api.post("/api/signup", {
        loginMethod: "google",
        email: decoded.email,
        name: decoded.name,
        googleSecret: googleCredential,
      });

      // Store tokens from response (Google signup auto-verifies)
      if (res.data.accessToken && res.data.refreshToken) {
        setTokens(res.data.accessToken, res.data.refreshToken);
      }

      await fetchUser();
      onClose();
      navigate("/dashboard");
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        const message: string = err.response.data?.message ?? "";
        toast.error(message || "An unexpected error occurred. Please try again.");
      } else {
        console.error(err);
        toast.error("Unable to connect. Please check your internet connection and try again.");
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="login-card" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          type="button"
          className="auth-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          &#x2715;
        </button>

        {/* Left hero pane — identical to Signup.tsx */}
        <div className="login-hero">
          <div className="login-badge">Get started</div>
          <h1 className="login-hero-title">Create your Napkin account.</h1>
          <p className="login-hero-subtitle">
            Set up your workspace in a few clicks. Send your first document for
            signature as soon as you&apos;re in the dashboard.
          </p>

          <ul className="login-hero-list">
            <li>
              <span className="login-hero-dot" />
              <span>
                <strong>Unlimited templates</strong> for your most-used NDAs,
                contracts, and agreements.
              </span>
            </li>
            <li>
              <span className="login-hero-dot" />
              <span>
                <strong>Simple tracking</strong> across all your in-progress
                signatures.
              </span>
            </li>
            <li>
              <span className="login-hero-dot" />
              <span>
                <strong>Team-ready</strong> when you&apos;re ready to invite
                collaborators.
              </span>
            </li>
          </ul>

          <div className="login-hero-footnote">
            You can change your plan or team settings anytime from the
            dashboard.
          </div>
        </div>

        {/* Right form pane — identical to Signup.tsx */}
        <div className="login-form-pane">
          <header className="login-header">
            <h2 className="login-title">Create your account</h2>
            <p className="login-subtitle">
              We&apos;ll use this to set up your workspace and keep your
              documents organized.
            </p>
          </header>

          <form className="login-form" onSubmit={handleSignupEmail}>
            <label className="login-field">
              <span className="login-label">Full name</span>
              <input
                type="text"
                id="modal-signup-name"
                name="name"
                placeholder="Jane Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
              />
            </label>

            <label className="login-field">
              <span className="login-label">Work email</span>
              <input
                type="email"
                id="modal-signup-email"
                name="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </label>

            <label className="login-field">
              <span className="login-label">Password</span>
              <input
                type="password"
                id="modal-signup-password"
                name="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              {password && (() => {
                const strength = getPasswordStrength(password);
                return (
                  <div style={{ marginTop: 6 }}>
                    <div style={{
                      height: 4,
                      borderRadius: 2,
                      background: '#e5e7eb',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${strength.percent}%`,
                        height: '100%',
                        borderRadius: 2,
                        background: strength.color,
                        transition: 'width 0.2s ease, background 0.2s ease',
                      }} />
                    </div>
                    <div style={{
                      fontSize: 11,
                      color: strength.color,
                      marginTop: 4,
                    }}>
                      {strength.label}
                    </div>
                  </div>
                );
              })()}
            </label>

            <button type="submit" className="login-primary-button">
              <span className="login-primary-text">Sign up</span>
            </button>

            <div className="login-divider">
              <span>or continue with</span>
            </div>

            <div className="login-google">
              <GoogleLogin
                onSuccess={(credentialResponse) => {
                  const token = credentialResponse.credential;
                  if (token) handleSignupGoogle(token);
                }}
                onError={() => {
                  toast.error("Google Signup Failed");
                }}
              />
            </div>
          </form>

          <p className="login-register">
            Already have an account?{" "}
            <button
              type="button"
              className="login-link login-link-strong"
              onClick={onSwitchToLogin}
            >
              Log in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignupModal;
